import React from 'react';
import { Link } from 'react-router-dom';
import { FaGift, FaArrowRight, FaClock, FaCheck, FaStar, FaFire } from 'react-icons/fa';
import Layout from '../components/Layout';
import SEO from '../components/SEO';

const Bonuses: React.FC = () => {
  const bonuses = [
    {
      bookmaker: 'Winamax',
      logo: '/images/offers/winamax.webp',
      url: 'https://www.winamax.fr/',
      title: 'Bonus de Bienvenue',
      amount: '150€',
      type: 'Match de dépôt',
      match: '100%',
      maxBonus: '150€',
      minDeposit: '10€',
      wagering: '3x',
      expiry: '30 jours',
      description: 'Le bonus le plus généreux du marché français. Déposez 10€ minimum et recevez jusqu\'à 150€ en bonus. Conditions de mise simples et claires.',
      code: 'WELCOME150',
      exclusive: true,
      rating: 4.9,
    },
    {
      bookmaker: 'Bwin',
      logo: '/images/offers/bwin.webp',
      url: 'https://bwin.fr/',
      title: 'Offre de Bienvenue',
      amount: '100€',
      type: 'Match de dépôt',
      match: '100%',
      maxBonus: '100€',
      minDeposit: '10€',
      wagering: '3x',
      expiry: '30 jours',
      description: 'Bonus classique mais efficace de Bwin. Parfait pour débuter avec une somme confortable. Conditions transparentes et retraits rapides.',
      code: null,
      exclusive: false,
      rating: 4.7,
    },
    {
      bookmaker: 'Unibet',
      logo: '/images/offers/unibet.webp',
      url: 'https://www.unibet.fr/',
      title: 'Bonus Nouveau Client',
      amount: '120€',
      type: 'Match de dépôt',
      match: '100%',
      maxBonus: '120€',
      minDeposit: '10€',
      wagering: '4x',
      expiry: '30 jours',
      description: 'Bonus solide d\'Unibet avec un programme de fidélité intégré. Idéal pour les joueurs réguliers qui souhaitent maximiser leurs gains.',
      code: null,
      exclusive: false,
      rating: 4.6,
    },
  ];

  const tips = [
    {
      icon: FaCheck,
      title: 'Lisez les conditions',
      description: 'Toujours vérifier les conditions de mise et les restrictions avant de réclamer un bonus.',
    },
    {
      icon: FaClock,
      title: 'Respectez les délais',
      description: 'Les bonus ont généralement une durée de validité limitée. Utilisez-les dans les temps.',
    },
    {
      icon: FaStar,
      title: 'Comparez les offres',
      description: 'Ne vous précipitez pas. Comparez les bonus et choisissez celui qui correspond à votre style de jeu.',
    },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Meilleurs Bonus de Paris Sportifs en France',
    description: 'Découvrez les meilleures offres promotionnelles des bookmakers français. Bonus de bienvenue, codes promo et conditions détaillées.',
    itemListElement: bonuses.map((bonus, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Offer',
        name: `${bonus.bookmaker} - ${bonus.title}`,
        description: bonus.description,
        price: bonus.amount,
        priceCurrency: 'EUR',
        availability: 'https://schema.org/InStock',
        seller: {
          '@type': 'Organization',
          name: bonus.bookmaker,
        },
      },
    })),
  };

  return (
    <Layout>
      <SEO
        title="Meilleurs Bonus de Paris Sportifs en France 2025"
        description="Découvrez les meilleurs bonus de bienvenue et codes promo des bookmakers français. Comparaison détaillée des offres promotionnelles."
        url="/bonuses"
        jsonLd={jsonLd}
      />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-purple-600/10 to-pink-600/10"></div>
        <div className="relative z-10 container mx-auto px-4 text-center">
          <div className="inline-block mb-6 px-4 py-2 glass rounded-full text-sm font-medium">
            <FaGift className="inline mr-2" />
            Offres Exclusives
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="text-gradient">Bonus & Promotions</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Maximisez vos gains dès le départ avec les meilleurs bonus de bienvenue du marché français. 
            Codes promo exclusifs et offres limitées.
          </p>
        </div>
      </section>

      {/* Bonuses Grid */}
      <section className="py-12 pb-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            {bonuses.map((bonus, index) => {
              const Icon = bonus.exclusive ? FaFire : FaGift;
              return (
                <article
                  key={index}
                  className="glass rounded-3xl p-8 card-hover relative overflow-hidden group"
                >
                  {bonus.exclusive && (
                    <div className="absolute top-4 right-4 px-3 py-1 bg-gradient-to-r from-orange-500 to-red-600 rounded-full text-xs font-bold flex items-center space-x-1">
                      <FaFire className="text-xs" />
                      <span>Exclusif</span>
                    </div>
                  )}

                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <div className="flex items-center space-x-3 mb-2">
                        <img 
                          src={bonus.logo} 
                          alt={bonus.bookmaker}
                          className="h-12 w-auto object-contain"
                          width="166"
                          height="48"
                          loading="lazy"
                          decoding="async"
                        />
                        <h2 className="text-2xl font-bold heading-font">{bonus.bookmaker}</h2>
                      </div>
                      <h3 className="text-xl text-gray-300 mb-1">{bonus.title}</h3>
                      <div className="flex items-center space-x-1">
                        <FaStar className="text-yellow-400 text-sm" />
                        <span className="text-sm font-semibold">{bonus.rating}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-4xl font-bold text-gradient">{bonus.amount}</div>
                    </div>
                  </div>

                  <p className="text-gray-300 mb-6 leading-relaxed">{bonus.description}</p>

                  {/* Bonus Details */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="glass rounded-xl p-4">
                      <div className="text-sm text-gray-400 mb-1">Type</div>
                      <div className="font-semibold">{bonus.type}</div>
                    </div>
                    <div className="glass rounded-xl p-4">
                      <div className="text-sm text-gray-400 mb-1">Match</div>
                      <div className="font-semibold">{bonus.match}</div>
                    </div>
                    <div className="glass rounded-xl p-4">
                      <div className="text-sm text-gray-400 mb-1">Dépôt min.</div>
                      <div className="font-semibold">{bonus.minDeposit}</div>
                    </div>
                    <div className="glass rounded-xl p-4">
                      <div className="text-sm text-gray-400 mb-1">Mise requise</div>
                      <div className="font-semibold">{bonus.wagering}</div>
                    </div>
                  </div>

                  {bonus.code && (
                    <div className="mb-6 p-4 glass rounded-xl">
                      <div className="text-sm text-gray-400 mb-2">Code promo</div>
                      <div className="font-mono text-lg font-bold text-gradient">{bonus.code}</div>
                    </div>
                  )}

                  <a
                    href={bonus.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full py-4 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 rounded-xl text-center font-bold text-white hover:shadow-xl hover:shadow-purple-500/50 transition-all flex items-center justify-center space-x-2"
                  >
                    <span>Réclamer le bonus</span>
                    <FaArrowRight />
                  </a>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tips Section */}
      <section className="py-12 pb-20">
        <div className="container mx-auto px-4">
          <div className="glass-strong rounded-3xl p-8 md:p-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
              <span className="text-gradient">Conseils</span> pour Optimiser vos Bonus
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {tips.map((tip, index) => {
                const Icon = tip.icon;
                return (
                  <div key={index} className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Icon className="text-2xl text-white" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">{tip.title}</h3>
                    <p className="text-gray-400">{tip.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Bonuses;

